import React, { useState, useEffect } from 'react';
import { Activity, Clock, CheckCircle, Pause, Play, XCircle, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import Layout from '../components/Layout';
import { useVisibility } from '../hooks/useVisibility';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProgressDashboard = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const isVisible = useVisibility();

  useEffect(() => {
    if (!isVisible) return undefined;
    fetchActiveTasks();
    // Refresh every 10 seconds for real-time updates
    const interval = setInterval(fetchActiveTasks, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const fetchActiveTasks = async () => {
    try {
      const response = await axios.get(`${API}/progress/active-tasks`, {
        headers: getAuthHeaders()
      });
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'in_progress':
        return <Activity className="w-5 h-5 text-blue-500 animate-pulse" />;
      case 'paused':
        return <Pause className="w-5 h-5 text-yellow-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-500';
      case 'in_progress':
        return 'text-blue-500';
      case 'paused':
        return 'text-yellow-500';
      case 'failed':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  const getSubTaskStatus = (subTasks) => {
    const total = subTasks.length;
    const completed = subTasks.filter(st => st.status === 'completed').length;
    const inProgress = subTasks.filter(st => st.status === 'in_progress').length;
    return { total, completed, inProgress };
  };

  const getEstimatedTime = (task) => {
    if (task.estimated_completion) {
      const now = new Date();
      const est = new Date(task.estimated_completion);
      const diffMs = est - now;
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) return 'Completing now...';
      if (diffMins < 60) return `${diffMins} minutes`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours} hours`;
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} days`;
    }
    return 'Calculating...';
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-blue-500" />
            Progress Dashboard
          </h1>
          <p className="text-gray-400 mt-1">Track all active tasks and their progress</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-800 p-4 rounded-lg">
            <div className="text-gray-400 text-sm">Active Tasks</div>
            <div className="text-3xl font-bold text-white mt-1">
              {tasks.filter(t => t.status === 'in_progress').length}
            </div>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg">
            <div className="text-gray-400 text-sm">Completed Today</div>
            <div className="text-3xl font-bold text-green-500 mt-1">
              {tasks.filter(t => {
                return t.status === 'completed' && 
                  new Date(t.completed_at).toDateString() === new Date().toDateString();
              }).length}
            </div>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg">
            <div className="text-gray-400 text-sm">Paused</div>
            <div className="text-3xl font-bold text-yellow-500 mt-1">
              {tasks.filter(t => t.status === 'paused').length}
            </div>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg">
            <div className="text-gray-400 text-sm">Avg Progress</div>
            <div className="text-3xl font-bold text-blue-500 mt-1">
              {tasks.length > 0
                ? Math.round(tasks.reduce((sum, t) => sum + t.progress_percent, 0) / tasks.length)
                : 0}%
            </div>
          </div>
        </div>

        {/* Tasks List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="text-gray-400 mt-4">Loading tasks...</p>
          </div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-12 bg-gray-800 rounded-lg">
            <Activity className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">No active tasks</p>
            <p className="text-gray-500 text-sm mt-2">Tasks will appear here when LEE starts working</p>
          </div>
        ) : (
          <div className="space-y-6">
            {tasks.map((task) => {
              const subTaskStats = getSubTaskStatus(task.sub_tasks || []);
              
              return (
                <div
                  key={task.id}
                  className="bg-gray-800 p-6 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors"
                >
                  {/* Task Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-start gap-3 flex-1">
                      {getStatusIcon(task.status)}
                      <div className="flex-1">
                        <h3 className="text-white font-semibold text-lg">{task.name}</h3>
                        <p className="text-gray-400 text-sm mt-1">{task.description}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-xs px-2 py-1 bg-gray-700 text-gray-300 rounded">
                            {task.category}
                          </span>
                          <span className={`text-xs font-medium ${getStatusColor(task.status)}`}>
                            {task.status.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                      </div>
                    </div>
                    {task.estimated_completion && (
                      <div className="text-right">
                        <div className="text-gray-400 text-xs">Est. completion</div>
                        <div className="text-white font-medium mt-1">
                          <Clock className="w-4 h-4 inline mr-1" />
                          {getEstimatedTime(task)}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-400 text-sm">Overall Progress</span>
                      <span className="text-white font-semibold">{task.progress_percent}%</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-blue-400 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${task.progress_percent}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Sub-tasks */}
                  {task.sub_tasks && task.sub_tasks.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-gray-300 font-medium text-sm">Sub-tasks</h4>
                        <span className="text-xs text-gray-500">
                          {subTaskStats.completed} of {subTaskStats.total} completed
                        </span>
                      </div>
                      <div className="space-y-2">
                        {task.sub_tasks.map((subTask) => (
                          <div
                            key={subTask.id}
                            className="flex items-center gap-3 p-3 bg-gray-900 rounded-lg"
                          >
                            {getStatusIcon(subTask.status)}
                            <div className="flex-1">
                              <div className="text-white text-sm">{subTask.name}</div>
                              {subTask.progress_percent > 0 && subTask.status === 'in_progress' && (
                                <div className="w-full bg-gray-700 rounded-full h-1 mt-2">
                                  <div
                                    className="bg-blue-500 h-1 rounded-full transition-all duration-300"
                                    style={{ width: `${subTask.progress_percent}%` }}
                                  ></div>
                                </div>
                              )}
                            </div>
                            <span className={`text-xs font-medium ${getStatusColor(subTask.status)}`}>
                              {subTask.status === 'completed' && '✓'}
                              {subTask.status === 'in_progress' && '⏳'}
                              {subTask.status === 'pending' && '⏸️'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Task Actions */}
                  <div className="flex gap-2 mt-4">
                    <button className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition-colors">
                      View Details
                    </button>
                    {task.status === 'in_progress' && (
                      <button className="px-4 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-500 rounded-lg text-sm transition-colors">
                        <Pause className="w-4 h-4 inline mr-1" />
                        Pause
                      </button>
                    )}
                    {task.status === 'paused' && (
                      <button className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-500 rounded-lg text-sm transition-colors">
                        <Play className="w-4 h-4 inline mr-1" />
                        Resume
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ProgressDashboard;
